import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '@/config/featureFlags';
import { isProductionRuntime } from '@/config/runtime';
import { debugLogger } from '@/utils/debug';

// Global AdSense script loader - loads once for the entire application
const AdSenseScript = () => {
  const location = useLocation();
  const isAdsEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_ADS);
  const isProd = isProductionRuntime();
  const isAuthCallbackRoute = location.pathname === '/auth/callback';
  const hasSensitiveAuthParams = location.hash.includes('access_token') || location.search.includes('access_token');

  useEffect(() => {
    if (!isAdsEnabled || !isProd) {
      debugLogger.log('[AdSenseScript] Ads disabled or dev environment - skipping script load');
      return;
    }

    if (isAuthCallbackRoute || hasSensitiveAuthParams) {
      debugLogger.log('[AdSenseScript] Skipping ad script on auth callback or while auth tokens are present in URL');
      return;
    }

    // Check if script already exists
    const existingScript = document.querySelector('script[src*="adsbygoogle.js"]');
    if (existingScript) {
      debugLogger.log('[AdSenseScript] AdSense script already loaded');
      return;
    }

    debugLogger.log('[AdSenseScript] Loading Google AdSense script globally');

    // Create and load the AdSense script
    const script = document.createElement('script');
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3120381243696579';
    script.async = true;
    script.crossOrigin = 'anonymous';
    
    script.onload = () => {
      debugLogger.log('[AdSenseScript] Google AdSense script loaded successfully');
      // Initialize the adsbygoogle array if it doesn't exist
      window.adsbygoogle = window.adsbygoogle || [];
    };
    
    script.onerror = () => {
      console.warn('[AdSenseScript] Google AdSense script failed to load - likely blocked by adblocker');
    };
    
    document.head.appendChild(script);

    return () => {
      // Don't remove the script on unmount as it's global
    };
  }, [hasSensitiveAuthParams, isAdsEnabled, isAuthCallbackRoute, isProd]);

  return null; // This component doesn't render anything
};

export default AdSenseScript;
